import { AlertTriangle, Bot, ChevronDown, Sparkles } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../LoadingSpinner';
import type { Job } from '../../modules/dashboard/components/JobCard';
import {
  type ExplanationStatus,
  type JobMatchExplanation,
  useJobMatchExplanation,
} from '../../modules/dashboard/hooks/useJobMatchExplanation';
import { useUserProfile } from '../../modules/dashboard/hooks/useUserProfile';
import {
  type ApplyRecommendation,
  computeApplyRecommendation,
} from '../../services/applyRecommendationService';
import type { UserProfile } from '../../types/userProfile';

export interface ShouldIApplyPanelProps {
  job: Job;
  profile?: UserProfile | null;
  explanation?: JobMatchExplanation | null;
  explanationStatus?: ExplanationStatus;
  onRefreshExplanation?: () => void;
  /** Pre-computed recommendation (e.g. marketing demo). Skips profile logic. */
  recommendation?: ApplyRecommendation;
  defaultExpanded?: boolean;
  className?: string;
  userId?: string | null;
}

function verdictLabelKey(verdict: ApplyRecommendation['verdict']): string {
  return `dashboard.jobs.shouldApply.verdict.${verdict}`;
}

function verdictFallback(verdict: ApplyRecommendation['verdict']): string {
  switch (verdict) {
    case 'yes_strong':
      return 'YES — Strong match';
    case 'yes':
      return 'YES — Good match';
    case 'maybe':
      return 'MAYBE — Partial match';
    case 'no':
      return 'NOT YET — Weak match';
    default:
      return 'MAYBE';
  }
}

function probabilityKey(level: ApplyRecommendation['interviewProbability']): string {
  return `dashboard.jobs.shouldApply.probability.${level}`;
}

function probabilityFallback(level: ApplyRecommendation['interviewProbability']): string {
  switch (level) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return 'Medium';
  }
}

const RecommendationBody: React.FC<{ recommendation: ApplyRecommendation }> = ({ recommendation }) => {
  const { t } = useTranslation();
  const verdictLabel = t(
    verdictLabelKey(recommendation.verdict),
    verdictFallback(recommendation.verdict),
  );
  const probabilityLabel = t(
    probabilityKey(recommendation.interviewProbability),
    probabilityFallback(recommendation.interviewProbability),
  );

  const verdictTone =
    recommendation.verdict === 'yes_strong' || recommendation.verdict === 'yes'
      ? 'positive'
      : recommendation.verdict === 'maybe'
        ? 'neutral'
        : 'negative';

  return (
    <div className="ds-should-apply__answer" role="region" aria-live="polite">
      <p className={`ds-should-apply__verdict ds-should-apply__verdict--${verdictTone}`}>
        {verdictLabel}
      </p>

      <div className="ds-should-apply__section">
        <p className="ds-should-apply__section-label">
          {t('dashboard.jobs.shouldApply.why', 'Why')}
        </p>
        <ul className="ds-should-apply__list">
          {recommendation.why.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      {recommendation.concern ? (
        <div className="ds-should-apply__section ds-should-apply__section--concern">
          <p className="ds-should-apply__section-label">
            <AlertTriangle className="w-3.5 h-3.5" aria-hidden />
            {t('dashboard.jobs.shouldApply.concern', 'Concern')}
          </p>
          <p className="ds-should-apply__concern-text">{recommendation.concern}</p>
        </div>
      ) : null}

      <div className="ds-should-apply__meta">
        <p>
          <span className="ds-should-apply__meta-label">
            {t('dashboard.jobs.shouldApply.interviewProbability', 'Estimated interview probability')}
          </span>
          <strong className={`ds-should-apply__probability ds-should-apply__probability--${recommendation.interviewProbability}`}>
            {probabilityLabel}
          </strong>
        </p>
        <p>
          <span className="ds-should-apply__meta-label">
            {t('dashboard.jobs.shouldApply.recommendedAction', 'Recommended action')}
          </span>
          <strong>{recommendation.recommendedAction}</strong>
        </p>
      </div>
    </div>
  );
};

const ShouldIApplyPanel: React.FC<ShouldIApplyPanelProps> = ({
  job,
  profile: profileProp,
  explanation: explanationProp,
  explanationStatus: explanationStatusProp,
  onRefreshExplanation,
  recommendation: recommendationProp,
  defaultExpanded = false,
  className = '',
  userId,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { profile: hookProfile } = useUserProfile();
  const profile = profileProp ?? hookProfile;

  const shouldFetchExplanation =
    recommendationProp == null
    && expanded
    && explanationProp === undefined
    && userId != null;

  const internalExplanation = useJobMatchExplanation(
    shouldFetchExplanation ? userId : null,
    shouldFetchExplanation ? job.id : null,
  );

  const explanation = explanationProp ?? internalExplanation.data;
  const explanationStatus = explanationStatusProp ?? internalExplanation.status;

  const recommendation = useMemo(() => {
    if (recommendationProp) return recommendationProp;
    return computeApplyRecommendation(job, profile, explanation);
  }, [recommendationProp, job, profile, explanation]);

  const isLoading =
    expanded
    && recommendationProp == null
    && (explanationStatus === 'loading' || explanationStatus === 'idle');

  const handleToggle = () => {
    setExpanded((prev) => !prev);
    if (!expanded && onRefreshExplanation && explanationStatus === 'error') {
      onRefreshExplanation();
    }
  };

  return (
    <section className={`ds-should-apply ${className}`.trim()} aria-label={t('dashboard.jobs.shouldApply.title', 'Should I apply?')}>
      <button
        type="button"
        className="ds-should-apply__trigger"
        onClick={handleToggle}
        aria-expanded={expanded}
      >
        <span className="ds-should-apply__trigger-icon" aria-hidden>
          <Bot className="w-4 h-4" />
        </span>
        <span className="ds-should-apply__trigger-label">
          {t('dashboard.jobs.shouldApply.button', 'Should I apply?')}
        </span>
        <Sparkles className="w-3.5 h-3.5 ds-should-apply__trigger-sparkle" aria-hidden />
        <ChevronDown
          className={`w-4 h-4 ds-should-apply__chevron${expanded ? ' ds-should-apply__chevron--open' : ''}`}
          aria-hidden
        />
      </button>

      {expanded ? (
        <div className="ds-should-apply__panel">
          {isLoading ? (
            <div className="ds-should-apply__loading">
              <LoadingSpinner size="sm" showText={false} />
              <p>{t('dashboard.jobs.shouldApply.thinking', 'Analysing your fit for this role…')}</p>
            </div>
          ) : (
            <RecommendationBody recommendation={recommendation} />
          )}
        </div>
      ) : null}
    </section>
  );
};

export default React.memo(ShouldIApplyPanel);
